import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import DashboardDetailLayout from './DashboardDetailLayout';
import NodeStatusMap from './artifactory/NodeStatusMap';
import PersonaContributions from './artifactory/PersonaContributions';
import HydrationTimeline from './artifactory/HydrationTimeline';
import QualityChurn from './artifactory/QualityChurn';
import ProvenanceLinks from './artifactory/ProvenanceLinks';
import api from '../../services/api';

const ArtifactoryDetail: React.FC = () => {
  const { manifestId } = useParams<{ manifestId: string }>();
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [manifest, setManifest] = useState<any>(null);
  const [personaNames, setPersonaNames] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!manifestId) return;
    api.get(`/artifactory/manifest/${manifestId}`)
      .then(r => setManifest(r.data))
      .catch(console.error);
    // Fetch persona names for contributor resolution
    api.get('/personas')
      .then(r => {
        const names: Record<string, string> = {};
        (r.data || []).forEach((p: any) => { names[p.id] = p.name || p.id; });
        setPersonaNames(names);
      })
      .catch(() => {});
  }, [manifestId]);

  if (!manifest || !manifestId) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-gray-900 text-gray-400' : 'bg-gray-50 text-gray-500'}`}>
        Loading manifest...
      </div>
    );
  }

  const nodes = Object.values(manifest.nodes || {}).map((node: any) => ({
    ...node,
    // path and name already exist on the node object
    // strip "project_root/" prefix for cleaner display
    path: (node.path || '').replace(/^project_root\//, ''),
    name: node.name || (node.path || '').split('/').pop() || '?',
  }));
  const st = (n: any) => (n.status || '').toLowerCase();
  const freshCount = nodes.filter((n: any) => st(n) === 'fresh').length;
  const projectName = manifest.project_name || manifest.metadata?.project_name || manifest.name || manifestId.slice(0, 8);

  return (
    <DashboardDetailLayout
      title={projectName}
      subtitle={`${nodes.length} nodes \u00b7 ${Math.round((freshCount / Math.max(nodes.length, 1)) * 100)}% hydrated`}
      backPath="/dashboards/artifactory"
      badges={
        <div className="flex items-center gap-2">
          <span className="px-2 py-0.5 text-xs rounded-full bg-green-600/20 text-green-300">FRESH: {freshCount}</span>
          <span className="px-2 py-0.5 text-xs rounded-full bg-gray-600/20 text-gray-300">
            GHOST: {nodes.filter((n: any) => st(n) === 'ghost').length}
          </span>
          <span className="px-2 py-0.5 text-xs rounded-full bg-amber-600/20 text-amber-300">
            STALE: {nodes.filter((n: any) => st(n) === 'stale').length}
          </span>
        </div>
      }
      actions={
        <button
          onClick={() => navigate(`/artifactory?project=${manifestId}`)}
          className="px-3 py-1.5 text-xs rounded-lg bg-gradient-to-r from-cyan-600 to-blue-600 text-white hover:from-cyan-700 hover:to-blue-700"
        >
          Open in Artifactory
        </button>
      }
      tabs={[
        { key: 'status', label: 'Node Status Map', content: <NodeStatusMap nodes={nodes} /> },
        { key: 'personas', label: 'Persona Contributions', content: <PersonaContributions nodes={nodes} manifestId={manifestId} personaNames={personaNames} /> },
        { key: 'timeline', label: 'Hydration Timeline', content: <HydrationTimeline manifestId={manifestId} /> },
        { key: 'quality', label: 'Quality & Churn', content: <QualityChurn nodes={nodes} /> },
        { key: 'provenance', label: 'Provenance & Links', content: <ProvenanceLinks manifestId={manifestId} /> },
      ]}
    />
  );
};

export default ArtifactoryDetail;
